import {
  HEARD_ABOUT_US_VALUES,
  type HeardAboutUs,
  formatMoneyMinor,
  heardAboutUsLabel,
} from "@repo/vihangam-yog-shared";
import { useCallback, useMemo, useState, type FormEvent } from "react";

import { track } from "../../analytics";
import { OCTOBER, octoberEvents } from "../../content/october";
import { requestErrorMessage } from "../../requestError";
import { trpc } from "../../trpc";
import { Button } from "../../ui/Button";
import { ContactLine } from "../../ui/ContactLine";
import { Disclosure } from "../../ui/Disclosure";
import { Field } from "../../ui/Field";

import { DayCards } from "./DayCards";
import { PartyEditor } from "./PartyEditor";
import { hawanQuote } from "./kunds";
import { LAST_HAWAN_OPTIONS, lastHawanLabel } from "./lastHawan";
import { pendingPayNow, savePendingPayNow } from "./payNowPending";
import {
  clearDraft,
  PAY_VIA,
  useBundleDraft,
  type BundleDraft,
  type PayVia,
} from "./useBundleDraft";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The typed age as a whole number of years, or null when it isn't one. */
function parseAge(text: string): number | null {
  const n = Number(text.trim());
  return text.trim() !== "" && Number.isInteger(n) && n >= 0 && n <= 120
    ? n
    : null;
}

/** The party as it will be sent: the self row carries the contact name. */
function partyWithNames(d: BundleDraft): BundleDraft["party"] {
  return d.party.map((p, i) =>
    d.selfIncluded && i === 0 ? { ...p, name: d.name } : p,
  );
}

/** What stops a submission: the message, and the id of the control (or
 *  group) to scroll to and focus so the person lands on it. */
interface FormProblem {
  target: string;
  message: string;
}

function firstProblem(d: BundleDraft): FormProblem | null {
  if (!d.name.trim()) {
    return { target: "reg-name", message: "Please enter your name." };
  }
  if (d.mobile.trim().length < 6) {
    return { target: "reg-mobile", message: "Please enter a mobile number." };
  }
  if (!EMAIL_RE.test(d.email.trim())) {
    return {
      target: "reg-email",
      message: "Please enter a valid email address.",
    };
  }
  const named = partyWithNames(d);
  if (!named.some((p) => p.kind === "adult" && p.name.trim())) {
    return { target: "reg-party", message: "Add at least one adult." };
  }
  const unnamed = named.findIndex((p) => !p.name.trim());
  if (unnamed >= 0) {
    return {
      target: `party-name-${unnamed}`,
      message: "Every person listed needs a name.",
    };
  }
  const ageless = d.party.findIndex((p) => parseAge(p.age) === null);
  if (ageless >= 0) {
    return {
      target: `party-age-${ageless}`,
      message: "Add an age for everyone, so we can plan seating and food.",
    };
  }
  // A child is under 16, an adult 16 or over: the same line that decides
  // who pays for Sunday, so the row kind and the age must agree.
  const mismatch = d.party.findIndex((p) => {
    const age = parseAge(p.age);
    return age !== null && (p.kind === "child" ? age >= 16 : age < 16);
  });
  if (mismatch >= 0) {
    const who = named[mismatch]?.name.trim() || "This person";
    return {
      target: `party-age-${mismatch}`,
      message:
        d.party[mismatch]?.kind === "child"
          ? `${who} is 16 or over, so add them as an adult.`
          : `${who} is under 16, so add them as a child.`,
    };
  }
  if (!d.stage && !d.hawan) {
    return { target: "reg-days", message: "Choose at least one day." };
  }
  if (d.hawan && !d.tierId) {
    return {
      target: "hawan-tier",
      message: "Choose how you'd like to take part on Sunday.",
    };
  }
  if (!d.heard) {
    return {
      target: "reg-heard",
      message: "Let us know how you heard about us.",
    };
  }
  if (d.hawan && !d.lastHawan) {
    return {
      target: "reg-last-hawan",
      message: "Tell us when you last did a Hawan.",
    };
  }
  if (!d.acknowledged) {
    return { target: "reg-ack", message: "Please tick the acknowledgement." };
  }
  return null;
}

/** Bring the offending control into view and put the cursor on it. A
 *  group (party, days, a radiogroup) focuses its first control. */
function goTo(target: string): void {
  const el = document.getElementById(target);
  if (!el) {
    return;
  }
  el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  const control =
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
      ? el
      : el.querySelector("input, select, textarea");
  if (control instanceof HTMLElement) {
    control.focus({ preventScroll: true });
  }
}

/** True when `value` is one of the catalog's referral options. */
function isHeardAboutUs(
  value: string,
): value is (typeof HEARD_ABOUT_US_VALUES)[number] {
  return HEARD_ABOUT_US_VALUES.some((v) => v === value);
}

/** The referral choices that came through someone, so a name applies:
 *  a person, or a WhatsApp forward (the organisers want to know who
 *  shared it). */
const HEARD_THROUGH_SOMEONE: readonly HeardAboutUs[] = [
  "friend",
  "family",
  "member",
  "whatsapp",
];

function heardThroughSomeone(heard: HeardAboutUs | ""): boolean {
  return heard !== "" && HEARD_THROUGH_SOMEONE.includes(heard);
}

/** The label for the name field; a forward was shared, not told. */
function heardDetailLabel(heard: HeardAboutUs | ""): string {
  return heard === "whatsapp"
    ? "Who shared it with you? (name)"
    : "Who told you? (name)";
}

/** A numbered heading over each part of the form, so the whole reads as
 *  five short steps rather than one long page. */
/** The two ways to pay for Sunday, PayNow first as the organisers asked. */
const PAY_OPTIONS = {
  paynow: {
    title: "PayNow",
    hint: "Scan a QR code with your bank app. You can save the code to your phone first.",
  },
  card: {
    title: "Card",
    hint: "Visa, Mastercard or American Express, on Stripe's secure page.",
  },
} satisfies Record<PayVia, { title: string; hint: string }>;

function Step({ n, title, lede }: { n: number; title: string; lede?: string }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-hairline pb-2">
      <span className="font-display text-sm text-magenta">{n}</span>
      <h2 className="font-display text-lg text-ink">{title}</h2>
      {lede ? <span className="text-sm text-ink-soft">{lede}</span> : null}
    </div>
  );
}

export function RegisterForm({
  resumeRef = null,
}: {
  resumeRef?: string | null;
}) {
  const [stage, hawan] = octoberEvents();
  const { draft, set: write, reset, restored } = useBundleDraft();
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<FormProblem | null>(null);
  // Any edit clears the pointer at the field that stopped the last submit.
  const set = useCallback(
    (patch: Partial<BundleDraft>) => {
      write(patch);
      setProblem(null);
    },
    [write],
  );
  const noteFor = (target: string) =>
    problem?.target === target ? problem.message : null;
  const [savedFreeDay, setSavedFreeDay] = useState<{
    ref: string;
    paymentError: string;
  } | null>(null);
  const register = trpc.registerEventBundle.useMutation();
  const availability = trpc.eventTierAvailability.useQuery(
    { eventSlug: OCTOBER.hawan.slug },
    { staleTime: 30_000 },
  );
  const soldOut = useMemo(
    () =>
      new Set(
        (availability.data ?? []).filter((a) => a.soldOut).map((a) => a.tierId),
      ),
    [availability.data],
  );

  if (!stage || !hawan) {
    return <p role="alert">The October events are not in the catalog.</p>;
  }
  const tier = hawan.price?.tiers.find((t) => t.id === draft.tierId);
  // The same ages the submit sends; an age not typed yet is charged, as
  // on the server, so the label never promises less than the checkout.
  const quoteParty = draft.party.map((p) => ({ age: parseAge(p.age) }));
  const quote =
    draft.hawan && tier && hawan.price
      ? hawanQuote(quoteParty, tier, hawan.price.currency)
      : null;
  const amount = quote
    ? formatMoneyMinor(quote.totalCents, hawan.price?.currency ?? "sgd")
    : null;
  const busy = register.isPending;
  const submitLabel = draft.hawan
    ? amount
      ? `Register and pay ${amount}`
      : "Register and pay"
    : "Register";

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const stop = firstProblem(draft);
    setProblem(stop);
    setError(null);
    if (stop) {
      goTo(stop.target);
      return;
    }
    const party = partyWithNames(draft).map((p) => ({
      name: p.name.trim(),
      kind: p.kind,
      // firstProblem() has already refused a party with a missing age.
      age: parseAge(p.age) ?? undefined,
      needs: p.needs.length > 0 ? p.needs : undefined,
      gender: p.gender ?? undefined,
    }));
    const note = draft.note.trim();
    const groupName = draft.groupName.trim();
    // Only meaningful beside someone; a stale name typed under "friend"
    // must not ride along once the choice moves to "Facebook".
    const heardDetail = heardThroughSomeone(draft.heard)
      ? draft.heardDetail.trim()
      : "";
    register.mutate(
      {
        name: draft.name.trim(),
        mobile: draft.mobile.trim(),
        email: draft.email.trim(),
        party,
        days: { stage: draft.stage, hawan: draft.hawan },
        hawanTierId: draft.hawan ? draft.tierId : undefined,
        foodPreference: draft.hawan && draft.food ? draft.food : undefined,
        heardAboutUs: draft.heard || undefined,
        heardAboutUsDetail: heardDetail || undefined,
        lastHawan: draft.lastHawan || undefined,
        whatsappJoined: draft.whatsappJoined,
        groupName: groupName || undefined,
        note: note || undefined,
        discountCode:
          draft.hawan && draft.discountCode.trim()
            ? draft.discountCode.trim()
            : undefined,
        payVia: draft.hawan ? draft.payVia : undefined,
        // A code this device is still waiting on is replaced by this
        // submission, so the server cancels it first.
        supersedes: draft.hawan ? (pendingPayNow() ?? undefined) : undefined,
        acknowledged: true,
      },
      {
        onSuccess: (r) => {
          track("register_submitted", {
            stage: draft.stage,
            hawan: draft.hawan,
          });
          if (r.payNow) {
            savePendingPayNow({
              paymentIntentId: r.payNow.paymentIntentId,
              clientSecret: r.payNow.clientSecret,
            });
            track("register_paid_redirect", { via: "paynow" });
            window.location.href = `/register/pay?intent=${encodeURIComponent(r.payNow.paymentIntentId)}`;
            return;
          }
          if (r.checkoutUrl) {
            try {
              sessionStorage.setItem("vysg-register-expect-hawan", "1");
            } catch {
              /* storage unavailable; the thanks page just won't poll */
            }
            track("register_paid_redirect", { via: "card" });
            window.location.href = r.checkoutUrl;
            return;
          }
          if (r.paymentError) {
            if (r.stageRegistered) {
              setSavedFreeDay({
                ref: r.confirmationRef,
                paymentError: r.paymentError,
              });
              return;
            }
            // Sunday-only submission: nothing was saved and no email went
            // out, so this must read as a plain failure, not a booking.
            setError(
              "We couldn't start the payment. Please try again, or contact us if it keeps happening.",
            );
            return;
          }
          clearDraft();
          window.location.href = `/register/thanks?ref=${encodeURIComponent(r.confirmationRef)}`;
        },
        onError: (err) =>
          setError(
            requestErrorMessage(
              err,
              "We couldn't start the payment. Please try again, or contact us if it keeps happening.",
            ),
          ),
      },
    );
  }

  if (savedFreeDay) {
    return (
      <div className="rounded-lg border border-teal bg-card p-6" role="status">
        <p className="eyebrow">Saturday is booked</p>
        <p className="mt-2 text-ink">
          Your registration for {stage.title} is saved under reference{" "}
          <strong className="font-mono">{savedFreeDay.ref}</strong> and an email
          is on its way.
        </p>
        <p className="mt-2 text-ink-soft">
          We couldn't start the payment for Sunday: {savedFreeDay.paymentError}
        </p>
        <div className="mt-4">
          <Button onClick={() => setSavedFreeDay(null)}>
            Try Sunday's payment again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-8">
      {resumeRef ? (
        <p className="rounded-md bg-ivory-deep p-3 text-sm text-ink">
          Welcome back. Your details are kept below; choose Sunday again to
          finish the payment. Reference {resumeRef}.
        </p>
      ) : restored ? (
        <p
          className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-ivory-deep p-3 text-sm text-ink"
          data-testid="draft-restored"
        >
          <span>We kept what you typed earlier on this device.</span>
          <button
            type="button"
            onClick={reset}
            className="font-medium text-teal-deep underline underline-offset-4"
          >
            Start again
          </button>
        </p>
      ) : null}
      <Step n={1} title="Your details" />
      <div className="grid gap-4 md:grid-cols-3">
        <Field
          label="Full name"
          htmlFor="reg-name"
          hint="This goes on your registration."
          error={noteFor("reg-name")}
        >
          <input
            id="reg-name"
            className="field"
            autoComplete="name"
            value={draft.name}
            disabled={busy}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>
        <Field
          label="Mobile"
          htmlFor="reg-mobile"
          hint="We may contact you on WhatsApp or by call, on the day or before."
          error={noteFor("reg-mobile")}
        >
          <input
            id="reg-mobile"
            className="field"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={draft.mobile}
            disabled={busy}
            onChange={(e) => set({ mobile: e.target.value })}
          />
        </Field>
        <Field
          label="Email"
          htmlFor="reg-email"
          hint="Your entry QR code will be sent to this email."
          error={noteFor("reg-email")}
        >
          <input
            id="reg-email"
            className="field"
            type="email"
            autoComplete="email"
            value={draft.email}
            disabled={busy}
            onChange={(e) => set({ email: e.target.value })}
          />
        </Field>
      </div>

      <Step n={2} title="Who is coming" />
      <PartyEditor
        party={draft.party}
        onChange={(party) => set({ party })}
        disabled={busy}
        selfName={draft.name}
        selfIncluded={draft.selfIncluded}
        onSelfIncludedChange={(selfIncluded) => set({ selfIncluded })}
        problem={problem}
      />

      <Step n={3} title="Which days" lede="One registration covers both." />
      <DayCards
        stage={stage}
        hawan={hawan}
        party={quoteParty}
        value={{
          stage: draft.stage,
          hawan: draft.hawan,
          tierId: draft.tierId,
          food: draft.food,
        }}
        dayNote={noteFor("reg-days")}
        tierNote={noteFor("hawan-tier")}
        onChange={(v) => set(v)}
        soldOut={soldOut}
        disabled={busy}
      />

      <Step n={4} title="A few questions" />
      <div className="grid gap-4">
        <div className="grid gap-4 self-start md:max-w-xl">
          <Field
            label="How did you hear about us?"
            htmlFor="reg-heard"
            error={noteFor("reg-heard")}
          >
            <select
              id="reg-heard"
              className="field"
              value={draft.heard}
              disabled={busy}
              onChange={(e) => {
                const heard = isHeardAboutUs(e.target.value)
                  ? e.target.value
                  : "";
                // The name only makes sense beside someone; clear it when
                // the choice moves to a channel.
                set(
                  heardThroughSomeone(heard)
                    ? { heard }
                    : { heard, heardDetail: "" },
                );
              }}
            >
              <option value="">Choose one</option>
              {HEARD_ABOUT_US_VALUES.map((v) => (
                <option key={v} value={v}>
                  {heardAboutUsLabel(v)}
                </option>
              ))}
            </select>
          </Field>
          {heardThroughSomeone(draft.heard) ? (
            <Field
              label={heardDetailLabel(draft.heard)}
              htmlFor="reg-heard-detail"
            >
              <input
                id="reg-heard-detail"
                className="field"
                maxLength={120}
                value={draft.heardDetail}
                disabled={busy}
                onChange={(e) => set({ heardDetail: e.target.value })}
              />
            </Field>
          ) : null}
          {draft.hawan ? (
            <fieldset>
              <legend className="float-none w-full p-0 text-sm font-semibold text-ink">
                When did you last do a Hawan?
              </legend>
              <p className="mt-1.5 text-xs leading-relaxed text-ink-soft">
                It helps us plan the guidance on the day.
              </p>
              <div
                id="reg-last-hawan"
                role="radiogroup"
                aria-label="When did you last do a Hawan?"
                className="mt-2.5 flex flex-wrap items-center gap-1.5"
              >
                {LAST_HAWAN_OPTIONS.map((o) => {
                  const on = draft.lastHawan === o;
                  return (
                    <label
                      key={o}
                      className={`flex min-h-11 cursor-pointer items-center rounded-full border px-3.5 text-sm transition-colors has-disabled:cursor-not-allowed has-disabled:opacity-50 has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-teal-deep ${on ? "border-magenta bg-magenta/5 font-medium text-ink" : "border-hairline-strong text-ink-soft hover:border-ink hover:text-ink"}`}
                    >
                      <input
                        type="radio"
                        name="last-hawan"
                        value={o}
                        className="sr-only"
                        checked={on}
                        disabled={busy}
                        onChange={() => set({ lastHawan: o })}
                      />
                      {lastHawanLabel(o)}
                    </label>
                  );
                })}
              </div>
              {noteFor("reg-last-hawan") ? (
                <p className="mt-1.5 text-sm text-crimson">
                  {noteFor("reg-last-hawan")}
                </p>
              ) : null}
            </fieldset>
          ) : null}
        </div>
      </div>

      <label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm text-ink">
        {/* Submitted as `whatsappJoined`: consent to be added to the group.
            The organisers send the link themselves once it exists. */}
        <input
          type="checkbox"
          checked={draft.whatsappJoined}
          disabled={busy}
          onChange={(e) => set({ whatsappJoined: e.target.checked })}
          className="mt-0.5 h-5 w-5 shrink-0 accent-magenta"
        />
        <span className="max-w-prose leading-relaxed">
          You can add me to the event WhatsApp group for updates.
          {OCTOBER.whatsappGroupUrl ? (
            <>
              {" "}
              <a
                href={OCTOBER.whatsappGroupUrl}
                target="_blank"
                rel="noreferrer"
                className="text-teal-deep underline underline-offset-4"
              >
                Open the group
              </a>
            </>
          ) : null}
        </span>
      </label>

      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Your group or organisation (optional)"
          htmlFor="reg-group"
          hint="A temple, a society, a company: whoever you are coming with."
        >
          <input
            id="reg-group"
            className="field"
            autoComplete="organization"
            maxLength={120}
            value={draft.groupName}
            disabled={busy}
            onChange={(e) => set({ groupName: e.target.value })}
          />
        </Field>

        <Field
          label="Anything we should know, or a question for us?"
          htmlFor="reg-note"
          hint="Access needs, an infant coming along, or anything else."
        >
          <textarea
            id="reg-note"
            className="field"
            rows={3}
            maxLength={400}
            value={draft.note}
            disabled={busy}
            onChange={(e) => set({ note: e.target.value })}
          />
        </Field>
      </div>

      <Step n={5} title="Confirm" />
      {draft.hawan ? (
        <fieldset id="reg-pay-via" className="grid gap-3">
          <legend className="text-sm font-semibold text-ink">
            How you'll pay for Sunday
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {PAY_VIA.map((id) => {
              const on = draft.payVia === id;
              return (
                <label
                  key={id}
                  className={`flex min-h-11 cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors has-disabled:cursor-not-allowed has-disabled:opacity-50 has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-teal-deep ${on ? "border-magenta bg-magenta/5" : "border-hairline hover:border-ink"}`}
                >
                  <input
                    type="radio"
                    name="pay-via"
                    value={id}
                    className="sr-only"
                    checked={on}
                    disabled={busy}
                    onChange={() => set({ payVia: id })}
                  />
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${on ? "border-magenta" : "border-hairline-strong"}`}
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full bg-magenta ${on ? "" : "opacity-0"}`}
                    />
                  </span>
                  <span>
                    <span className="block font-semibold text-ink">
                      {PAY_OPTIONS[id].title}
                    </span>
                    <span className="mt-0.5 block text-sm leading-relaxed text-ink-soft">
                      {PAY_OPTIONS[id].hint}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}
      {draft.hawan ? (
        <Field label="Discount code" htmlFor="hawan-discount-code">
          <input
            id="hawan-discount-code"
            className="field max-w-xs uppercase"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={40}
            value={draft.discountCode}
            disabled={busy}
            onChange={(e) => set({ discountCode: e.target.value })}
          />
        </Field>
      ) : null}

      <Disclosure summary="Refunds and your data" look="card">
        <div className="grid gap-3 text-sm text-ink-soft">
          <div>
            <p className="font-semibold text-ink">Refunds</p>
            {OCTOBER.policies.refunds.map((line) => (
              <p key={line} className="mt-1 max-w-prose">
                {line}
              </p>
            ))}
          </div>
          <div>
            <p className="font-semibold text-ink">Your details</p>
            {OCTOBER.policies.privacy.map((line) => (
              <p key={line} className="mt-1 max-w-prose">
                {line}
              </p>
            ))}
          </div>
        </div>
      </Disclosure>

      <label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm text-ink">
        <input
          id="reg-ack"
          type="checkbox"
          checked={draft.acknowledged}
          disabled={busy}
          onChange={(e) => set({ acknowledged: e.target.checked })}
          className="mt-0.5 h-5 w-5 shrink-0 accent-magenta"
          aria-label="I acknowledge"
          aria-invalid={noteFor("reg-ack") ? true : undefined}
        />
        <span className="max-w-prose leading-relaxed">
          I acknowledge that the details above are correct, that Vihangam Yoga
          Singapore may contact me about these events, and I agree to the refund
          policy and the use of my details described above.
        </span>
      </label>

      {(problem ?? error) ? (
        <p
          role="alert"
          className="rounded-md border border-crimson/40 bg-crimson/5 p-3 text-sm text-crimson"
        >
          {problem?.message ?? error}
        </p>
      ) : null}
      {register.error && !error && !problem ? (
        <p
          role="alert"
          className="rounded-md border border-crimson/40 bg-crimson/5 p-3 text-sm text-crimson"
        >
          {register.error.message}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? "Registering" : submitLabel}
        </Button>
        <p className="text-xs text-ink-soft">
          {draft.hawan
            ? "Cards and PayNow, through Stripe."
            : "No payment for Saturday."}
        </p>
      </div>
      <ContactLine />
    </form>
  );
}
